import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom'; 
import { CartProvider } from './context/CartContext'; 
import Navbar from './components/Navbar';
import ProductCard from './components/ProductCard';
import CategoryGrid from './components/CategoryGrid';
import Footer from './components/Footer';
import SideMenu from './components/SideMenu';
import AdminPanel from './components/AdminPanel';
import ProductDetail from './components/ProductDetail';
import CollectionView from './components/CollectionView';
import CustomerAuth from './components/CustomerAuth'; 
import WishlistPage from './components/WishlistPage'; 
import CartPage from './components/CartPage';        
import CheckoutForm from './components/CheckoutForm'; // 🌟 RAZORPAY INTEGRATED CHECKOUT FORM
import OrderHistoryPage from './components/OrderHistoryPage'; // 🌟 Add this import
import ShippingPolicyPage from './components/ShippingPolicyPage';
import ReturnsPolicyPage from './components/ReturnsPolicyPage';
import ContactPage from './components/ContactPage';
import TermsConditionsPage from './components/TermsConditionsPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';

// 🌟 SCROLL-TO-TOP COMPONENT: Automatically scrolls to the top of the window on route transition
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// HOMEPAGE HERO AND PRODUCT DISPLAY CONTENT 
const HomepageHeroAndGrid = ({ products }) => {
  const navigate = useNavigate(); 

  return (
    <>
      {/* HIGH-END EDITORIAL HERO SECTION */}
      <main className="flex flex-col items-center justify-center pt-44 pb-24 px-6 text-center bg-[#F5F5DC]">
        
        {/* Established Date Tag */}
        <h2 className="text-[10px] uppercase tracking-[0.6em] text-[#664229] mb-10 opacity-70 font-bold">
          EST. 2024
        </h2>
        
        {/* NATIVE PERFECTLY BLENDED HERO IMAGE PORTAL */}
        <div className="w-full max-w-[550px] md:max-w-[650px] h-auto mb-16 flex items-center justify-center overflow-hidden">
          <img 
            src="/logo_final.jpg" 
            alt="Modische Kledings Official Brand Label"
            className="w-full h-auto object-contain"
          />
        </div>
        
        {/* Interactive CTA Button */}
        <button 
          onClick={() => navigate('/collection/search')}
          className="border border-[#664229] text-[#664229] px-16 py-5 text-[10px] uppercase tracking-[0.4em] hover:bg-[#664229] hover:text-[#F5F5DC] transition-all duration-700 font-bold cursor-pointer"
        >
          Explore Collection
        </button>
      </main>

      {/* Category Selection Grid Section */}
      <CategoryGrid />

      {/* Featured Arrivals Grid Showcase */}
      <section className="max-w-[1600px] mx-auto px-6 pb-32">
        <div className="flex justify-between items-end mb-12">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold tracking-tighter text-[#2C1E12]">Featured Arrivals</h2>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#664229] opacity-60 font-medium">New Season 2024</p>
          </div>
          
          <span 
            onClick={() => navigate('/collection/search')}
            className="text-[10px] uppercase tracking-widest text-[#664229] border-b border-[#664229] pb-1 hover:opacity-50 transition font-bold cursor-pointer"
          >
            View All Items
          </span>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {products && products.length > 0 ? (
            products.map(product => (
              <ProductCard 
                key={product._id || product.id} 
                id={product._id} 
                name={product.name}
                price={product.price}
                discountPrice={product.discountPrice} 
                images={product.images || [product.imageUrl]} 
              />
            ))
          ) : (
            <div className="col-span-full text-center py-12 text-[#664229]/60 text-xs tracking-widest uppercase">
              Your store is currently empty. Open the Admin Form to upload items!
            </div>
          )}
        </div>
      </section>
    </>
  );
};

// PRIMARY APP ROUTER CORE SYSTEM
function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false); 
  const [products, setProducts] = useState([]); 

  // ABSOLUTE MAXIMUM SAFETY LOCK: Protects against literal "null" strings or unauthenticated sessions
  const checkAdminPrivileges = () => {
    try {
      const targetUser = localStorage.getItem('modische_user');
      
      // If storage is completely empty, or explicitly set to ghost string words, kill it instantly
      if (!targetUser || targetUser === 'null' || targetUser === 'undefined' || targetUser === '{}') {
        return false;
      }

      const parsed = JSON.parse(targetUser);
      // Double check that the object itself exists and strictly contains the true flag
      if (parsed && typeof parsed === 'object' && (parsed.isAdmin === true || parsed.role === 'admin')) {
        return true;
      }
      
      return false;
    } catch (e) {
      console.error("Critical session shield crash prevented:", e);
      return false; 
    }
  };

  const showAdminToggle = checkAdminPrivileges();
  useEffect(() => {
    fetch('https://api.modischekledings.com/api/products') 
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(err => console.error("Error connecting to your MongoDB backend:", err));
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <CartProvider>
        {/* 🌟 FIXED VIEWPORT BLOCK SHIELD: Enforces page boundaries globally across mobile devices */}
        <div className="min-h-screen bg-[#F5F5DC] w-full max-w-full overflow-x-hidden relative text-center">
          
          {/* SECURITY SHIELD: Only authorized staff profile tokens can reveal this interactive switch button link */}
          {showAdminToggle && (
            <button 
              onClick={() => setIsAdminView(!isAdminView)} 
              className="fixed bottom-6 right-6 z-[100] bg-[#664229] text-[#F5F5DC] text-[8px] uppercase tracking-widest p-3 shadow-xl rounded-sm font-bold font-mono border-none cursor-pointer">
              {isAdminView ? "Go to Live Store" : "Open Admin Form"}
            </button>
          )}

          {isAdminView && showAdminToggle ? (
            <React.Fragment>
              <Navbar onMenuClick={() => setIsMenuOpen(true)} />
              <SideMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} onSelectCategory={() => {}} />
              {/* 🌟 FIXED INNER WRAPPER FRAME: Insulates dashboard layers against size breaks */}
              <main className="w-full max-w-full overflow-hidden block">
                <AdminPanel />
              </main>
            </React.Fragment>
          ) : (
            <>
              <Navbar onMenuClick={() => setIsMenuOpen(true)} />
              <SideMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

              {/* 🌟 FIXED INNER WRAPPER FRAME: Insulates catalog lists and registers from sizing breaks */}
              <main className="w-full max-w-full overflow-hidden block">
                <Routes>
                  {/* PRIMARY HOMEPAGE ROUTE ELEMENT ASSIGNMENT */}
                  <Route path="/" element={<HomepageHeroAndGrid products={products} />} />

                  {/* ROUTE SINGLE PRODUCT ITEM VIEWS */}
                  <Route path="/product/:id" element={<ProductDetail />} />

                  <Route path="/my-orders" element={<OrderHistoryPage />} />

                  {/* ROUTE DYNAMIC FILTER PATH SELECTIONS */}
                  <Route path="/collection/:type" element={<CollectionView products={products} />} />

                  {/* PAGES FOR CART & WISHLIST VIEWS */}
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/admin" element={<AdminPanel />} />
                  <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
                  <Route path="/returns-policy" element={<ReturnsPolicyPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

                  {/* RAZORPAY INTEGRATED CHECKOUT FORM COMPONENT ENTRY POINT */}
                  <Route path="/checkout" element={
                    <div className="min-h-screen bg-[#F5F5DC] pt-40 pb-20 px-6 flex items-center justify-center">
                      <CheckoutForm />
                    </div>
                  } />

                  {/* DYNAMIC CUSTOMER LOGIN AND REGISTRATION SIGN-IN PORTAL ROUTES */}
                  <Route path="/auth" element={<CustomerAuth />} />
                  <Route path="/login" element={<CustomerAuth />} />
                </Routes>
              </main>

              <Footer />
            </>
          )}
        </div>
      </CartProvider>
    </Router>
  );
}

export default App;